import { useState } from 'react'
import moment from 'moment'
import { useFormik, FormikProvider } from 'formik'
import * as yup from 'yup'

import {
  PUT_UPDATE_CONTRACTOR_KEY,
  putUpdateContractor,
} from '../payrollActions'
import { getContractorName } from '../helpers'
import { PayrollError } from '../PayrollError'
import { formatCurrency } from '@/utils/currencyHelpers'
import { Button, Dialog, Form, Text } from '@/ui'
import { DetailsRow } from '../employee/JobPayTab'
import { DATE_FORMATS } from '@/utils/dateHelpers'
import {
  Gusto_Contractor,
  Gusto_ContractorPayment,
} from '../generated_gusto_types'
import { useAppDispatch } from '@/utils/typeHelpers'
import { TypographyStyles } from '@/styles'

// TODO: Migrate to V2
import {
  makeNumberSchema,
  makeReqStringSchema,
} from '@/componentsV1/BaseComponents'

const END_VAL = 'for this contractor'
const validationSchema = yup.object({
  first_name: yup
    .string()
    .nullable()
    .when('type', ([type], schema) =>
      type === 'Individual'
        ? makeReqStringSchema({ field: 'first name', end: END_VAL })
        : schema
    ),
  last_name: yup
    .string()
    .nullable()
    .when('type', ([type], schema) =>
      type === 'Individual'
        ? makeReqStringSchema({ field: 'last name', end: END_VAL })
        : schema
    ),
  business_name: yup
    .string()
    .nullable()
    .when('type', ([type], schema) =>
      type === 'Business'
        ? makeReqStringSchema({ field: 'business name', end: END_VAL })
        : schema
    ),
  ein: yup
    .string()
    .nullable()
    .when('type', ([type], schema) =>
      type === 'Business'
        ? makeNumberSchema({
            field: 'EIN',
            numDigits: 9,
            end: END_VAL,
            allowLeadingZero: true,
          })
        : schema
    ),
  hourly_rate: yup.string().when('wage_type', ([wage_type], schema) =>
    wage_type === 'Hourly'
      ? makeNumberSchema({
          field: 'hourly rate',
          end: END_VAL,
          allowedDecimals: 2,
        })
      : schema
  ),
})

const UpdateContractorModal = ({
  contractor,
  closeModal,
  modalOpen,
}: {
  contractor: Gusto_Contractor
  closeModal: () => void
  modalOpen: boolean
}) => {
  const dispatch = useAppDispatch()

  const formik = useFormik({
    initialValues: {
      first_name: contractor.first_name,
      last_name: contractor.last_name,
      business_name: contractor.business_name,
      ein: contractor.ein,
      hourly_rate: contractor.hourly_rate,
      wage_type: contractor.wage_type,
      // Not edited but used for validation
      type: contractor.type,
    },
    validationSchema,
    onSubmit: async (values) => {
      const res = await putUpdateContractor(contractor.uuid, {
        version: contractor.version,
        ...values,
      })(dispatch)

      if (res) {
        closeModal()
      }
    },
  })

  const isIndividual = contractor.type === 'Individual'

  return (
    <Dialog open={modalOpen} onOpenChange={closeModal}>
      <Dialog.Content className="max-w-md space-y-6">
        <Dialog.Title>Update Details</Dialog.Title>

        <FormikProvider value={formik}>
          <div className="space-y-4">
            <Text>
              This information is used to report the contractor’s taxes to the
              government. Please make sure it is accurate.
            </Text>

            <PayrollError fetchKey={PUT_UPDATE_CONTRACTOR_KEY} />

            {isIndividual && (
              <>
                <Form.Input name="first_name" label="First name" required />
                <Form.Input name="last_name" required label="Last name" />
              </>
            )}

            {!isIndividual && (
              <>
                <Form.Input
                  name="business_name"
                  required
                  label="Business name"
                />
                <Form.Input name="ein" required label="Ein" />
              </>
            )}

            <Form.Select
              options={[
                { value: 'Hourly', text: 'Hourly' },
                { value: 'Fixed', text: 'Fixed' },
              ]}
              name="wage_type"
              required
              label="Wage Type"
            />

            {formik.values.wage_type === 'Hourly' && (
              <Form.Input
                name="hourly_rate"
                componentType="currency"
                label="Hourly Rate"
                required
              />
            )}

            <div className="space-y-2">
              <Button onClick={() => formik.handleSubmit()} className="w-full">
                Confirm
              </Button>
              <Button
                onClick={closeModal}
                variant="secondary"
                className="w-full"
              >
                Cancel
              </Button>
            </div>
          </div>
        </FormikProvider>
      </Dialog.Content>
    </Dialog>
  )
}

const ContractorDetailsPanel = ({
  contractor,
}: {
  contractor: Gusto_Contractor
}) => {
  const [modalOpen, setIsModalOpen] = useState(false)

  return (
    <div className="gep-details-container space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
        <Text variant={TypographyStyles.H2}>Contractor Details</Text>
        <Button variant="link" onClick={() => setIsModalOpen(true)}>
          Edit
        </Button>
      </div>

      <div className="space-y-4">
        <DetailsRow
          label="Full Name"
          value={getContractorName(contractor) || ''}
        />
        {contractor.type === 'Business' && (
          <DetailsRow label="Ein" value={contractor.ein || ''} />
        )}
        <DetailsRow label="Contractor Type" value={contractor.type} />
        <DetailsRow label="Wage Type" value={contractor.wage_type} />
        {contractor.wage_type === 'Hourly' && (
          <DetailsRow
            label="Hourly Rate"
            value={formatCurrency(contractor.hourly_rate)}
          />
        )}
        <DetailsRow label="Email" value={contractor.email} />
        <DetailsRow
          label="Contractor Status"
          value={contractor.is_active ? 'Active' : 'Inactive'}
        />
      </div>

      {modalOpen && (
        <UpdateContractorModal
          contractor={contractor}
          modalOpen
          closeModal={() => setIsModalOpen(false)}
        />
      )}
    </div>
  )
}

const ContractorPayments = ({
  payments,
  goToPayments,
}: {
  payments: Gusto_ContractorPayment[]
  goToPayments: () => void
}) => (
  <div className="gep-details-container">
    <div className="flex flex-col gap-4">
      <Text variant={TypographyStyles.H2}>Payments</Text>
      <div className="flex flex-col gap-2">
        {payments.slice(0, 5).map((payment) => (
          <Text key={payment.date}>
            {moment(payment.date).format(DATE_FORMATS.DISPLAY_LONG)} –{' '}
            {formatCurrency(payment.wage_total)}
          </Text>
        ))}
      </div>

      {Boolean(payments.length) && (
        <div className="w-full sm:w-1/2">
          <Button className="w-full" onClick={goToPayments}>
            View All
          </Button>
        </div>
      )}
    </div>
  </div>
)

export const ContractorDetailsTab = ({
  contractor,
  payments,
  goToPayments,
}: {
  contractor: Gusto_Contractor
  payments: Gusto_ContractorPayment[]
  goToPayments: () => void
}) => (
  <div className="flex flex-col lg:flex-row gap-6">
    <div className="w-full lg:w-1/2">
      <ContractorDetailsPanel contractor={contractor} />
    </div>
    <div className="w-full lg:w-1/2">
      <ContractorPayments payments={payments} goToPayments={goToPayments} />
    </div>
  </div>
)
